import { Plus } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { listInboxes } from '../../api/endpoints';
import type { Inbox } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { RefreshButton } from '../../components/RefreshButton';
import { SmsDefaultForm } from '../../components/SmsDefaultForm';
import { Spinner } from '../../components/Spinner';
import { CapabilitiesContext } from '../../lib/capabilities';
import { channelHealth, healthCounts, identityLine } from '../../lib/channelMeta';
import { useLoad } from '../../lib/useLoad';
import { useOauthReturn } from '../../oauth/useOauthReturn';
import { ChannelCard } from './ChannelCard';
import './channels.css';

/** The OAuth-return banner: verify against the freshly loaded inboxes. */
function OauthReturnBanner({ inboxes }: { inboxes: Inbox[] | null }) {
  const outcome = useOauthReturn();
  const [dismissed, setDismissed] = useState(false);
  if (!outcome || dismissed) return null;

  if (outcome.kind === 'error') {
    return (
      <div className="home-banner">
        <Note tone="danger">
          {outcome.message}{' '}
          {outcome.marker && (
            <Link to={`/inboxes/${outcome.marker.inboxId}`}>Open the inbox to retry.</Link>
          )}
        </Note>
      </div>
    );
  }

  // 'returned' — success appends nothing, so trust only the refetched data.
  if (!inboxes) return null; // still loading; banner renders once data lands
  const target = inboxes.find((inbox) => inbox.id === outcome.marker.inboxId);
  if (!target) return null;
  const healthy = channelHealth(target) === 'connected';
  return (
    <div className="home-banner">
      {healthy ? (
        <Note tone="ok">
          <strong>{identityLine(target)}</strong> is connected and ready to use.{' '}
          <button className="linklike" onClick={() => setDismissed(true)}>
            Dismiss
          </button>
        </Note>
      ) : (
        <Note tone="warn">
          The sign-in for <strong>{target.name}</strong> didn’t finish.{' '}
          <Link to={`/inboxes/${target.id}`}>Open the inbox to try again.</Link>
        </Note>
      )}
    </div>
  );
}

export function ChannelsHome() {
  const { data, loading, refreshing, error, reload } = useLoad(listInboxes);
  const [actionError, setActionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const capabilities = useContext(CapabilitiesContext);
  // Unknown capabilities count as yes, as on the manage screen: the server answers either way.
  const mayChangeSmsDefault = capabilities?.me ? capabilities.me.access.features.connectChannels : true;

  const counts = useMemo(() => (data ? healthCounts(data) : null), [data]);
  const subtitle = counts
    ? counts.connected + counts.attention === 0
      ? 'Connect your first channel to start receiving conversations.'
      : `${counts.connected} connected${counts.attention ? ` · ${counts.attention} need${counts.attention === 1 ? 's' : ''} attention` : ''}`
    : ' ';

  return (
    <main className="page">
      <OauthReturnBanner inboxes={data} />
      <div className="page-head">
        <div>
          <h1 className="page-title">Channels</h1>
          <p className="page-sub">{subtitle}</p>
        </div>
        <div className="page-head-actions">
          {/* Channel health is decided by background polls and webhooks, so this
              page goes stale while it sits open — reconnect banners included. */}
          <RefreshButton refreshing={refreshing} busy={loading} onClick={reload} />
          <Link to="/connect" className="btn btn-primary">
            <Plus size={15} />
            Connect a channel
          </Link>
        </div>
      </div>

      {data && data.length > 0 && (
        <div className="home-banner">
          <SmsDefaultForm
            inboxes={data}
            canChange={mayChangeSmsDefault}
            onOpenInbox={(id) => navigate(`/inboxes/${id}`)}
            reload={() => reload()}
          />
        </div>
      )}

      {actionError && (
        <div className="home-banner">
          <ErrorNote error={actionError} />
        </div>
      )}

      {loading && !data && (
        <div className="home-loading">
          <Spinner large />
        </div>
      )}
      {error && (
        <div className="home-banner">
          <ErrorNote error={error} />
          <button className="btn btn-secondary" onClick={reload} style={{ marginTop: 10 }}>
            Try again
          </button>
        </div>
      )}

      {data && data.length === 0 && !error && (
        <div className="card card-pad home-empty">
          <h2>No channels yet</h2>
          <p>
            Connect a mailbox, phone number, or messaging bot and its conversations appear in the
            dashboard’s Communications page.
          </p>
          <Link to="/connect" className="btn btn-primary">
            <Plus size={15} />
            Connect a channel
          </Link>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="channel-list">
          {data.map((inbox) => (
            <ChannelCard key={inbox.id} inbox={inbox} onError={setActionError} />
          ))}
        </div>
      )}
    </main>
  );
}
